import { Text } from '@thanx/uikit/text'
import React, { useCallback, useEffect, useState } from 'react'
import { StyleObject, useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'

// Device width. Paired with PHONE_FIXED_HEIGHT_PX this is a 1:2 frame; keep the
// two in step if either changes, since the ratio is the thing that makes it read
// as a phone rather than a panel.
export const PHONE_WIDTH_PX = 300

// Device-height floor: below this the phone stops reading as a device and the
// message list has room for barely two bubbles.
export const PHONE_MIN_HEIGHT_PX = 560

// Device-height ceiling for callers that want a fixed-height phone. Without a
// definite max the frame grows with the conversation (the whole flex chain is
// min-height only), so the inner scroller never clips; capping here bounds the
// device so overflow-y:auto takes over and the message list scrolls instead.
export const PHONE_MAX_HEIGHT_PX = 600

// Device bezel + corner radius, measured off the Figma export (plan §1.1: sub-pixel
// ink coverage gives 6.87 and 6.95 across the two frames; the topmost-row corner
// inset of 19.6/20.1 inverts `r - √(r - 0.25)` to ~25). The outer 300x600 was
// already correct — only this chrome was, at 10px/36px, visibly chunkier.
export const PHONE_BEZEL_WIDTH_PX = 7
export const PHONE_BEZEL_RADIUS_PX = 25

// App-header geometry. The brand row height is PINNED rather than left to its
// content, because a logo is an <img> (no line box) and a name is text (a 20px line
// box) — unpinned, the message list would start at a different y for the two states
// and the phone would appear to shift when a merchant's logo finished loading.
// 9 + 20 + 9 = 38px, plus the 1px rule, which is what the Figma measures.
const PHONE_HEADER_PAD_Y_PX = 9
const PHONE_HEADER_PAD_X_PX = 16
const PHONE_HEADER_ROW_HEIGHT_PX = 20
// Holds a square or tall logo inside the pinned row (the Figma wordmark inks at 13px).
const PHONE_BRAND_LOGO_MAX_HEIGHT_PX = 16

// EVERY phone surface pins the device rather than capping it. A min+max pair
// leaves a 40px band whose resolved value follows the grid row (and with it the
// right column's content), so the device resized as content filled in beside it
// — visibly, while the merchant watched. Pinned to the existing ceiling on
// purpose: the Tuning tab and the wizard's free phase already resolved to it, so
// pinning changes the height of nothing, only its stability.
//   context: the pin is also what keeps WizardTour's re-measure MutationObserver
//   idempotent — an unpinned phone growing 560→600px mid-tour would make the
//   spotlight rect genuinely change and the coach mark would jump.
//   context: originally scoped to the scripted demo (R2/D2). Widened to all
//   surfaces after review — the band was the root cause of AI-931's cropped
//   preview on the post-setup Tuning tab, and a flag-scoped pin left the default
//   path still moving.
export const PHONE_FIXED_HEIGHT_PX = PHONE_MAX_HEIGHT_PX

// Extracted + exported so the height decision is unit-testable directly —
// styletron's css() is a no-op in the jsdom test env, so the emitted rule can't
// be asserted through the rendered DOM.
export const buildPhoneHeightStyle = ({
  fixedHeightPx,
  maxHeightPx,
}: {
  fixedHeightPx?: number
  maxHeightPx?: number
}): StyleObject => {
  if (fixedHeightPx != null) {
    return {
      height: `${fixedHeightPx}px`,
      minHeight: `${fixedHeightPx}px`,
      maxHeight: `${fixedHeightPx}px`,
      // min-height already floors flex shrinking; the explicit 0 says "this
      // device never resizes" to the next reader.
      flexShrink: 0,
    }
  }
  return {
    height: '100%',
    minHeight: `${PHONE_MIN_HEIGHT_PX}px`,
    maxHeight: maxHeightPx != null ? `${maxHeightPx}px` : undefined,
  }
}

type Props = {
  // Brand shown in the phone's app header. Also the <img> alt when a logo is
  // supplied, so this stays required even for a logo-bearing merchant.
  brandName: string
  /**
   * The merchant's logo. Figma puts the merchant's wordmark here rather than any
   * text, so it wins when present and `brandName` is the fallback.
   *
   * Resolve it through `useMerchantBrand`, which already strips Paperclip's
   * `missing.png` sentinel — a logo-less merchant does NOT get a null URL, so a
   * bare truthiness check here would render a broken-image box.
   */
  brandLogoUrl?: string
  // Optional coach-mark anchor value (`data-tour`) so a tour can spotlight the
  // whole device — the wizard passes TOUR_ANCHORS.simulator here.
  dataTour?: string
  // Optional PIN (px): height, min-height and max-height all collapse onto this
  // one value, so the device cannot resize with its content. Takes precedence
  // over `maxHeightPx`. Omitted → today's 560-600px band (D4).
  fixedHeightPx?: number
  // Optional fixed height cap (px). When set the phone stops growing with the
  // conversation and the message list scrolls inside it (AI-582 — the wizard
  // demo and the standalone sandbox both pass it); omitted → the frame sizes to
  // its grid cell.
  maxHeightPx?: number
  children: React.ReactNode
}

// Device-frame chrome for the wizard sandbox: wraps the conversation in a phone
// mockup so the merchant previews what the CUSTOMER sees (Figma wizard-testing).
// Presentational only — the conversation + input arrive as `children` (a "bare"
// ChatPanel that omits its own card border, since the phone provides the frame).
// The bezel/header use grey90 + grey20 palette tokens (a device frame, not a
// themed surface). NOT a React.FC.
const PhoneFrame = ({
  brandName,
  brandLogoUrl,
  dataTour,
  fixedHeightPx,
  maxHeightPx,
  children,
}: Props): JSX.Element => {
  const [css] = useStyletron()
  // Sentinel-stripping in useMerchantBrand is necessary but NOT sufficient: it only
  // recognises Paperclip's `missing.*` filename, and a merchant can just as easily hold
  // a real-looking URL whose asset 404s (a re-pointed bucket, a deleted upload, a CDN
  // blip). That renders as a broken-image glyph next to the alt text in the phone's
  // header — worse than the name it replaced. So the load failure is handled too, and
  // the name is the fallback.
  const [logoFailed, setLogoFailed] = useState(false)
  const handleLogoError = useCallback(() => setLogoFailed(true), [])
  // A merchant switch (or a re-upload) hands down a different URL, so the previous
  // failure must not suppress it.
  useEffect(() => setLogoFailed(false), [brandLogoUrl])
  const showLogo = brandLogoUrl !== undefined && !logoFailed

  return (
    <div
      data-tour={dataTour}
      className={css({
        // 300 x PHONE_FIXED_HEIGHT_PX is 1:2, which reads as a current phone.
        // At the previous 340px the frame was 1:1.76 — real, but iPhone-SE
        // proportions, which looked squat next to what people picture as a phone
        // (Darshan review: "fat and pudgy"). Narrowed rather than made taller on
        // purpose: growing the device would regrow the grid row, re-open the
        // tour-spotlight re-measure, and worsen reflow at 200% zoom.
        maxWidth: `${PHONE_WIDTH_PX}px`,
        margin: '0 auto',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...buildPhoneHeightStyle({ fixedHeightPx, maxHeightPx }),
        backgroundColor: merchantTheme.colors.white,
        border: `${PHONE_BEZEL_WIDTH_PX}px solid ${merchantTheme.colors.grey90}`,
        borderRadius: `${PHONE_BEZEL_RADIUS_PX}px`,
        overflow: 'hidden',
      })}
    >
      {/* App header — the brand the customer would see at the top of the chat.
          Logo first, name as the fallback: the device previews the CUSTOMER's view,
          and a customer sees the merchant, never "RecoveryAI". */}
      <div
        className={css({
          padding: `${PHONE_HEADER_PAD_Y_PX}px ${PHONE_HEADER_PAD_X_PX}px`,
          height: `${PHONE_HEADER_ROW_HEIGHT_PX + PHONE_HEADER_PAD_Y_PX * 2}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: `1px solid ${merchantTheme.colors.grey20}`,
          flex: '0 0 auto',
        })}
      >
        {showLogo ? (
          // alt is the brand name, not "logo": the header's job is to say WHOSE chat
          // this is, so that is what a screen reader should hear.
          <img
            src={brandLogoUrl}
            alt={brandName}
            onError={handleLogoError}
            className={css({
              display: 'block',
              maxHeight: `${PHONE_BRAND_LOGO_MAX_HEIGHT_PX}px`,
              maxWidth: '100%',
            })}
          />
        ) : (
          <Text.BodyText4 bold color="grey90">
            {brandName}
          </Text.BodyText4>
        )}
      </div>

      {/* Conversation + input fill the remaining device height. minHeight:0 lets
          the inner scroll region shrink correctly inside the flex column. */}
      <div
        className={css({
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        })}
      >
        {children}
      </div>
    </div>
  )
}

export default PhoneFrame
